// src/components/Auth/SignIn.jsx

import React, { useState } from 'react';

import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  CircularProgress,
  Grid,
} from '@mui/material';

import {
  Visibility,
  VisibilityOff,
  Google,
  Facebook,
  Apple,
} from '@mui/icons-material';

import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';

import { useApp } from '../../contexts/AppContext';
import { HypeCircleLogo } from '../Branding';
import { validateEmail, validatePassword, checkRateLimit } from '../../utils/authValidation';
import { useAuthSecurity } from '../../hooks/useAuthSecurity';
import SecurityAlert from './SecurityAlert';
import { clearAllCache } from '../../utils/cacheUtils';

// Amplify v6 modular imports
import { signIn, getCurrentUser, signOut, signInWithRedirect } from 'aws-amplify/auth';
// Remove the sync function - AuthContext handles user creation automatically

const SignIn = ({ onToggleAuthMode }) => {
  const { addNotification } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    securityState, 
    checkSubmissionLimit, 
    logSecurityEvent, 
    getSecurityRecommendations 
  } = useAuthSecurity();
  
  // Get return URL from location state
  const returnUrl = location.state?.from?.pathname || '/movies';

  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [lastSubmitTime, setLastSubmitTime] = useState(0);
  const submitTimeoutRef = React.useRef(null);
  const hangDetectionRef = React.useRef(null);

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'identifier':
        error = validateEmail(value);
        break;
      case 'password':
        error = validatePassword(value);
        break;
      default:
        break;
    }
    setErrors(prev => ({ ...prev, [name]: error }));
    return !error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear field-specific errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    // Clear API errors when user modifies form
    if (errors.api) {
      setErrors(prev => ({ ...prev, api: '' }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const resetForm = () => {
    setLoading(false);
    setErrors({});
  };

  const isFormValid = React.useMemo(() => {
    const hasIdentifier = formData.identifier.trim().length > 0;
    const hasPassword = formData.password.length > 0;
    const hasFieldErrors = Object.entries(errors).some(([key, error]) => key !== 'api' && error && error !== '');
    
    return hasIdentifier && hasPassword && !hasFieldErrors;
  }, [formData.identifier, formData.password, errors]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check rate limiting
    const rateLimitCheck = checkSubmissionLimit(formData.identifier, 'signin');
    if (!rateLimitCheck.allowed) {
      setErrors({ 
        api: `Too many attempts. Please wait ${rateLimitCheck.waitTime} seconds before trying again.` 
      });
      logSecurityEvent('rate_limit_exceeded', { email: formData.identifier });
      return;
    }

    // Prevent double submission
    const now = Date.now();
    if (loading || (now - lastSubmitTime < 2000)) {
      return;
    }
    setLastSubmitTime(now);

    // Clear any existing errors
    setErrors({});

    const isIdentifierValid = validateField('identifier', formData.identifier);
    const isPasswordValid = validateField('password', formData.password);
    if (!isIdentifierValid || !isPasswordValid) {
      logSecurityEvent('validation_failed', { email: formData.identifier });
      return;
    }

    setLoading(true);
    logSecurityEvent('signin_attempt', { email: formData.identifier });

    // Set up hang detection - if sign-in takes more than 30 seconds, show error
    hangDetectionRef.current = setTimeout(() => {
      if (loading) {
        setLoading(false);
        setErrors({ 
          api: '⏰ Sign-in is taking longer than expected. This might be due to browser password manager interference. Please try again or refresh the page.' 
        });
        addNotification({
          type: 'warning',
          message: 'Sign-in timeout. Please try again.',
          duration: 5000
        });
      }
    }, 30000);

    try {
      // Clear cache before sign-in to prevent cache-related issues
      try {
        await clearAllCache();
        console.log('Cache cleared before sign-in');
      } catch (cacheError) {
        console.warn('Failed to clear cache:', cacheError);
        // Continue with sign-in even if cache clear fails
      }
      
      // Use email as username for Cognito
      const username = formData.identifier.trim();
      
      // signIn via Amplify Auth instance
      const result = await signIn({ username, password: formData.password });

      // Check if sign-in was successful
      if (result?.isSignedIn) {
        logSecurityEvent('signin_success', { email: formData.identifier });
        
        addNotification({
          type: 'success',
          message: 'Welcome back!',
        });

        // Small delay to allow auth state to update, then navigate
        setTimeout(() => {
          window.location.href = returnUrl;
        }, 250);
      } else {
        throw new Error('Sign in was not completed successfully');
      }

    } catch (error) {
      console.error('Sign in error:', error);
      logSecurityEvent('signin_failed', { 
        email: formData.identifier, 
        error: error.name,
        message: error.message 
      });
      
      let errorMessage = 'Sign in failed. Please try again.';

      switch (error.name) {
        case 'NotAuthorizedException':
          errorMessage = '❌ Incorrect username or password. Please check your credentials and try again.';
          break;
        case 'UserNotConfirmedException':
          errorMessage = '📧 Please confirm your email address before signing in. Check your inbox for the confirmation code.';
          // Offer to resend confirmation code
          setTimeout(() => {
            addNotification({
              type: 'info',
              message: 'Need a new confirmation code? Go to Sign Up and we can resend it.',
              duration: 8000
            });
          }, 2000);
          break;
        case 'UserNotFoundException':
          errorMessage = '🔍 No account found with this email address. Please sign up for a new account first.';
          break;
        case 'TooManyRequestsException':
          errorMessage = '⏰ Too many failed attempts. Please wait a few minutes before trying again.';
          break;
        case 'UserAlreadyAuthenticatedException':
          // Handle stale session - sign out and retry
          try {
            console.log('🔄 Stale session detected, signing out and retrying...');
            await signOut();
            await clearAllCache();
            // Retry sign-in after clearing session
            const retryResult = await signIn({ username: formData.identifier.trim(), password: formData.password });
            if (retryResult?.isSignedIn) {
              logSecurityEvent('signin_success', { email: formData.identifier });
              addNotification({
                type: 'success',
                message: 'Welcome back! (Session refreshed)',
              });
              // Small delay to allow auth state to update, then navigate
              setTimeout(() => {
                window.location.href = returnUrl;
              }, 250);
              return; // Exit early on success
            }
          } catch (retryError) {
            console.error('Retry sign-in failed:', retryError);
            errorMessage = '🔄 Session conflict resolved. Please try signing in again.';
          }
          break;
        case 'NetworkError':
          errorMessage = '🌐 Network error. Please check your internet connection and try again.';
          break;
        default:
          errorMessage = error.message || errorMessage;
      }

      // Clear any existing errors and set the new API error
      setErrors({ api: errorMessage });
      
      // Show user-friendly notification
      addNotification({
        type: 'error',
        message: errorMessage,
        duration: 8000
      });
    } finally {
      // Clear hang detection timeout
      if (hangDetectionRef.current) {
        clearTimeout(hangDetectionRef.current);
        hangDetectionRef.current = null;
      }
      
      // Always re-enable the form with a small delay
      submitTimeoutRef.current = setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  };

  // Cleanup timeouts on unmount
  React.useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
      if (hangDetectionRef.current) {
        clearTimeout(hangDetectionRef.current);
      }
    };
  }, []);

  // Handle browser password manager autofill
  React.useEffect(() => {
    const handleFormChange = () => {
      // Clear API errors when form changes (e.g., password manager fills form)
      if (errors.api) {
        setErrors(prev => ({ ...prev, api: '' }));
      }
    };

    const form = document.querySelector('form');
    if (form) {
      form.addEventListener('input', handleFormChange);
      form.addEventListener('change', handleFormChange);
      
      return () => {
        form.removeEventListener('input', handleFormChange);
        form.removeEventListener('change', handleFormChange);
      };
    }
  }, [errors.api]);

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      logSecurityEvent('google_signin_attempt', {});
      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      console.error('Google sign in error:', error);
      logSecurityEvent('google_signin_failed', { error: error.message });
      addNotification({
        type: 'error',
        message: 'Failed to sign in with Google. Please try again.',
      });
      setLoading(false);
    }
  };

  const handleFacebookSignIn = async () => {
    try {
      setLoading(true);
      logSecurityEvent('facebook_signin_attempt', {});
      await signInWithRedirect({ provider: 'Facebook' });
    } catch (error) {
      console.error('Facebook sign in error:', error);
      logSecurityEvent('facebook_signin_failed', { error: error.message });
      addNotification({
        type: 'error',
        message: 'Failed to sign in with Facebook. Please try again.',
      });
      setLoading(false);
    }
  };

  return (
    <Card sx={{ 
      maxWidth: { xs: '100%', sm: 450, md: 500, lg: 550 }, 
      width: '100%',
      mx: 'auto', 
      mt: { xs: 0, sm: 3 }, 
      borderRadius: { xs: 0, sm: 3 }, 
      boxShadow: { xs: 0, sm: 3 }
    }}>
      <CardContent sx={{ 
        p: { xs: 2, sm: 3, md: 4 },
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box textAlign="center" mb={{ xs: 2, sm: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <HypeCircleLogo size="lg" variant="default" />
          </Box>
          <Typography 
            variant={{ xs: 'h5', sm: 'h4' }} 
            component="h1" 
            fontWeight="bold" 
            gutterBottom
            sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}
          >
            Welcome Back
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ 
              fontSize: { xs: '0.95rem', sm: '1rem' },
              maxWidth: { xs: '100%', sm: '80%', md: '70%' },
              mx: 'auto'
            }}
          >
            Sign in to your account to continue
          </Typography>
        </Box>

        {/* Google Sign In Button */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Google />}
          onClick={handleGoogleSignIn}
          disabled={loading}
          sx={{ 
            mb: 2, 
            py: 1.5,
            borderColor: '#4285f4',
            color: '#4285f4',
            '&:hover': {
              borderColor: '#357ae8',
              backgroundColor: 'rgba(66, 133, 244, 0.04)'
            }
          }}
        >
          Continue with Google
        </Button>

        {/* Facebook Sign In Button */}
{/*         <Button
          fullWidth
          variant="outlined"
          startIcon={<Facebook />}
          onClick={handleFacebookSignIn}
          disabled={loading}
          sx={{ 
            mb: 2, 
            py: 1.5,
            borderColor: '#1877f2',
            color: '#1877f2',
            '&:hover': {
              borderColor: '#166fe5',
              backgroundColor: 'rgba(24, 119, 242, 0.04)'
            }
          }}
        >
          Continue with Facebook
        </Button>

        <Divider sx={{ my: 2 }}>or</Divider> */}

        {/* Security Alerts */}
        {securityState.suspiciousActivity && (
          <SecurityAlert 
            type="suspicious-activity" 
            onDismiss={() => {}} 
            persistent={true}
          />
        )}
        
        {securityState.isRateLimited && (
          <SecurityAlert 
            type="account-security" 
            onDismiss={() => {}} 
          />
        )}

        {/* Error Alert */}
        {errors.api && (
          <Alert 
            severity="error" 
            sx={{ mt: 2 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={resetForm}
                disabled={loading}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                }}
              >
                Try Again
              </Button>
            }
          >
            {errors.api}
          </Alert>
        )}

        {/* Loading Indicator */}
        {loading && (
          <Alert 
            severity="info" 
            sx={{ mt: 2 }}
            icon={<CircularProgress size={16} />}
          >
            {errors.api ? 'Clearing cache and signing you in...' : 'Signing you in... Please wait and don\'t refresh the page.'}
          </Alert>
        )}

        {/* Sign In Form */}
        <Box 
          component="form" 
          onSubmit={handleSubmit} 
          sx={{ mt: 1 }}
          autoComplete="on"
          noValidate
        >
          <TextField
            label="Email Address"
            name="identifier"
            type="email"
            value={formData.identifier}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.identifier}
            helperText={errors.identifier || 'Enter your email address'}
            fullWidth
            margin="normal"
            disabled={loading}
            autoComplete="email"
            autoFocus
            sx={{ mb: 2 }}
            placeholder="email@example.com"
          />
          <TextField
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.password}
            helperText={errors.password}
            fullWidth
            margin="normal"
            disabled={loading}
            autoComplete="current-password"
            sx={{ mb: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mb: 2 }}>
            <Link
              component={RouterLink}
              to="/forgot-password"
              variant="body2"
              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Forgot password?
            </Link>
          </Box>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={!isFormValid || loading}
            sx={{ 
              mt: 3, 
              py: { xs: 2, sm: 1.5 }, 
              fontSize: { xs: '1rem', sm: '1.1rem' }, 
              fontWeight: 600,
              transition: 'all 0.2s ease',
              '&:disabled': {
                opacity: 0.6,
                cursor: 'not-allowed'
              }
            }}
            onClick={(e) => {
              // Prevent browser password manager from interfering
              e.stopPropagation();
            }}
          >
            {loading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} color="inherit" />
                Signing In...
              </Box>
            ) : errors.api ? (
              '🔄 Try Again'
            ) : (
              '🚀 Sign In'
            )}
          </Button>
          
          {/* Force Cache Clear Button for troubleshooting */}
          {errors.api && (
            <Button
              variant="outlined"
              color="warning"
              fullWidth
              disabled={loading}
              onClick={async () => {
                try {
                  setLoading(true);
                  await clearAllCache();
                  addNotification({
                    type: 'success',
                    message: 'Cache cleared successfully. Please try signing in again.',
                    duration: 4000
                  });
                  setErrors({});
                } catch (error) {
                  addNotification({
                    type: 'error',
                    message: 'Failed to clear cache. Please refresh the page.',
                    duration: 4000
                  });
                } finally {
                  setLoading(false);
                }
              }}
              sx={{ 
                mt: 1, 
                py: 1, 
                fontSize: '0.9rem',
                textTransform: 'none'
              }}
            >
              🧹 Clear Cache & Try Again
            </Button>
          )}
        </Box>

        {/* Anonymous User Redirect */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            🎬 Want to explore movies first?
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/movies')}
            sx={{ 
              mb: 2,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '0.9rem'
            }}
          >
            Browse Movies & Shows
          </Button>
        </Box>

        <Box sx={{ mt: { xs: 2, sm: 2 }, textAlign: 'center' }}>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.9rem', sm: '0.875rem' } }}
          >
            Don&apos;t have an account?{' '}
            <Link 
              component="button" 
              variant="body2" 
              onClick={onToggleAuthMode}
              sx={{ 
                textDecoration: 'none', 
                fontWeight: 'medium',
                fontSize: { xs: '0.9rem', sm: '0.875rem' },
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Sign up here
            </Link>
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SignIn;
